"use client";

import { Button } from "./ui/button";
import WorkStatus from "./work-status";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const CalCom = dynamic(() => import("./cal.com"), {
  loading: () => <Button variant="default">Book a Call</Button>
});

interface CTAProps {
  heading?: string;
  description?: string;
  showWorkStatus?: boolean;
  ctaPrimary?: {
    text?: string;
    actionType?: "link" | "calcom";
    url?: string;
  };
  ctaSecondary?: {
    text?: string;
    actionType?: "link" | "calcom";
    url?: string;
  };
  siteSettings?: {
    theme?: "light" | "dark";
    available?: boolean;
    availableLabel?: string;
    unavailableLabel?: string;
  } | null;
  styling?: {
    backgroundColor?: string;
    customBackgroundColor?: string;
    textColor?: string;
    customTextColor?: string;
    padding?: string;
    borderRadius?: string;
  };
}

export default function CTA({
  heading = "Let's talk about your next project",
  description = "Need a Web Developer? Let's get started!",
  showWorkStatus = true,
  ctaPrimary,
  ctaSecondary,
  siteSettings: initialSiteSettings,
  styling = {},
}: CTAProps) {
  const [siteSettings, setSiteSettings] = useState<{
    theme?: "light" | "dark";
    available?: boolean;
    availableLabel?: string;
    unavailableLabel?: string;
  } | null>(initialSiteSettings ?? null);

  useEffect(() => {
    if (initialSiteSettings) {
      setSiteSettings(initialSiteSettings);
      return;
    }

    const loadSiteSettings = async () => {
      try {
        const response = await fetch("/api/public-content?type=siteSettings", {
          method: "GET",
          cache: "force-cache",
        });
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as {
          theme?: "light" | "dark";
          available?: boolean;
          availableLabel?: string;
          unavailableLabel?: string;
        } | null;
        setSiteSettings(data);
      } catch {
        setSiteSettings(null);
      }
    };

    loadSiteSettings();
  }, [initialSiteSettings]);

  const {
    backgroundColor = "bg-bg-secondary",
    customBackgroundColor,
    textColor = "text-white",
    customTextColor,
    padding = "",
    borderRadius = "rounded-2xl sm:rounded-3xl",
  } = styling;

  const backgroundStyle =
    backgroundColor === "custom" && customBackgroundColor
      ? { backgroundColor: customBackgroundColor }
      : {};

  const textStyle =
    textColor === "custom" && customTextColor ? { color: customTextColor } : {};

  return (
    <section className="py-8 sm:py-12 md:py-16">
      <div className="container mx-auto px-6">
        <div
          className={`${padding} ${borderRadius} ${backgroundColor} ${textColor} text-center relative overflow-visible`}
          style={{ ...backgroundStyle, ...textStyle }}
        >
          <div className="px-4 sm:px-6 md:px-8 pt-8 sm:pt-10 md:pt-12 pb-8 sm:pb-10 md:pb-12">
            {showWorkStatus && (
              <div className="mb-8 sm:mb-10 md:mb-12 flex justify-center">
                <WorkStatus
                  display="static"
                  theme={siteSettings?.theme === "light" ? "light" : "dark"}
                  status={siteSettings?.available ? "available" : "unavailable"}
                  availableLabel={siteSettings?.availableLabel}
                  unavailableLabel={siteSettings?.unavailableLabel}
                />
              </div>
            )}

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium mb-4 sm:mb-5 md:mb-6 leading-tight">
              {heading}
            </h2>

            <p className="text-sm sm:text-base md:text-lg mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>

            {(ctaPrimary?.text || ctaSecondary?.text) && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                {ctaPrimary?.text &&
                  (ctaPrimary?.actionType === "calcom" ? (
                    <CalCom
                      triggerText={ctaPrimary.text}
                      triggerVariant="default"
                    />
                  ) : (
                    <Button variant="default" asChild className="w-full sm:w-auto">
                      <a href={ctaPrimary.url || "#"}>{ctaPrimary.text}</a>
                    </Button>
                  ))}

                {ctaSecondary?.text &&
                  (ctaSecondary?.actionType === "calcom" ? (
                    <CalCom
                      triggerText={ctaSecondary.text}
                      triggerVariant="outline"
                    />
                  ) : (
                    <Button variant="outline" asChild className="w-full sm:w-auto">
                      <a href={ctaSecondary.url || "#"}>{ctaSecondary.text}</a>
                    </Button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
